// Empire Apps Chassis · connectors/financial/mfapi-extended
// MFAPI.in (free) for latest NAV by scheme code + AMFI Direct daily NAVAll.txt
// (manual or local-host cached) + CAMS CAS upload bridge.
//
// MFAPI: https://api.mfapi.in/mf  (public, no key, returns JSON)
// AMFI:  https://www.amfiindia.com/spages/NAVAll.txt  (pipe-delimited)

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type MutualFundFolio,
  fail,
  folioId,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';
import { getPdfBridge, RE_INR } from './adobe-pdf-bridge.ts';

const STATUS_KEY = 'empire:financial:mfapi-extended:status';
const MFAPI_BASE = 'https://api.mfapi.in/mf';
const AMFI_URL = 'https://www.amfiindia.com/spages/NAVAll.txt';

interface PersistedStatus {
  readonly connected: boolean;
  readonly lastSync: string | null;
  readonly folioCount: number;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, lastSync: null, folioCount: 0 });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

interface MfApiHistoryPoint {
  readonly date: string;
  readonly nav: string;
}

interface MfApiResponse {
  readonly meta: {
    readonly scheme_code: string;
    readonly scheme_name: string;
    readonly fund_house: string;
  };
  readonly data: readonly MfApiHistoryPoint[];
}

export async function fetchLatestNav(schemeCode: string): Promise<{ readonly nav: number; readonly date: string; readonly name: string; readonly amc: string } | null> {
  try {
    const r = await fetch(`${MFAPI_BASE}/${encodeURIComponent(schemeCode)}/latest`);
    if (!r.ok) return null;
    const j = (await r.json()) as MfApiResponse;
    const first = j.data[0];
    if (!first) return null;
    const nav = Number(first.nav);
    if (!Number.isFinite(nav)) return null;
    return { nav, date: first.date, name: j.meta.scheme_name, amc: j.meta.fund_house };
  } catch {
    return null;
  }
}

export interface AmfiNavRow {
  readonly schemeCode: string;
  readonly schemeName: string;
  readonly amc: string;
  readonly nav: number;
  readonly date: string;
}

export function parseAmfiNavAll(text: string): readonly AmfiNavRow[] {
  const out: AmfiNavRow[] = [];
  let currentAmc = '';
  for (const lineRaw of text.split(/\r?\n/)) {
    const line = lineRaw.trim();
    if (!line) continue;
    if (!line.includes(';') && !line.startsWith('Scheme')) {
      if (line.length > 5) currentAmc = line;
      continue;
    }
    const parts = line.split(';');
    if (parts.length < 5) continue;
    const schemeCode = parts[0]?.trim() ?? '';
    if (!/^\d{4,}$/.test(schemeCode)) continue;
    const schemeName = parts[3]?.trim() ?? '';
    const nav = Number(parts[4]?.trim() ?? 'NaN');
    const date = parts[7]?.trim() ?? '';
    if (!Number.isFinite(nav)) continue;
    out.push({ schemeCode, schemeName, amc: currentAmc, nav, date });
  }
  return out;
}

export interface CamsCasHolding {
  readonly amc: string;
  readonly schemeName: string;
  readonly folioNumber: string;
  readonly units: number;
  readonly currentValuePaise: number;
  readonly costPaise: number;
}

export function parseCamsCasText(text: string): readonly CamsCasHolding[] {
  const out: CamsCasHolding[] = [];
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  let amcCur = '';
  for (const line of lines) {
    if (/^[A-Z][A-Za-z &.]+(?:Mutual Fund|MF|Asset Management|AMC)$/.test(line)) {
      amcCur = line;
      continue;
    }
    const folioMatch = line.match(/Folio[:\s]+([A-Z0-9\/-]+)/i);
    const inrTokens = [...line.matchAll(RE_INR)];
    RE_INR.lastIndex = 0;
    const unitsMatch = line.match(/(\d+\.\d{3,4})\s*units/i);
    if (folioMatch && unitsMatch && inrTokens.length >= 2) {
      out.push({
        amc: amcCur,
        schemeName: line.split(/\s{2,}/)[0] ?? line.slice(0, 50),
        folioNumber: folioMatch[1] ?? 'unknown',
        units: Number(unitsMatch[1]),
        currentValuePaise: parseInrPaise(inrTokens[inrTokens.length - 1]?.[0] ?? '0'),
        costPaise: parseInrPaise(inrTokens[0]?.[0] ?? '0'),
      });
    }
  }
  return out;
}

function parseInrPaise(s: string): number {
  const m = s.match(/[\d,]+(?:\.\d{1,2})?/);
  if (!m) return 0;
  return Math.round(Number(m[0].replace(/,/g, '')) * 100);
}

function mockFolios(): readonly MutualFundFolio[] {
  const seed: ReadonlyArray<readonly [string, string, string, number, number, number, number]> = [
    ['HDFC AMC', 'HDFC Flexi Cap Fund - Direct - Growth', '120503', 1845.234, 1825, 8420000, 4500000],
    ['Parag Parikh', 'PPFAS Flexi Cap - Direct - Growth', '122639', 524.881, 6420, 7250000, 4800000],
    ['Mirae Asset', 'Mirae Large & Midcap - Direct - Growth', '118989', 412.502, 9810, 8350000, 5200000],
    ['SBI MF', 'SBI Small Cap - Direct - Growth', '125497', 218.345, 19850, 8170000, 4300000],
    ['Nippon India', 'Nippon Small Cap - Direct - Growth', '113177', 198.234, 18920, 6890000, 3850000],
    ['ICICI Pru', 'ICICI Pru Tech - Direct - Growth', '120322', 825.554, 5680, 8230000, 5800000],
  ];
  return seed.map(([amc, name, code, units, navRupees, currentValue, invested]) =>
    Object.freeze<MutualFundFolio>({
      folioId: folioId(`folio_${code}`),
      amc,
      schemeCode: code,
      schemeName: name,
      units,
      latestNavPaise: paise(navRupees * 100),
      currentValuePaise: paise(currentValue),
      investedPaise: paise(invested),
      asOf: isoTs(),
    }),
  );
}

export const mfapiExtended: Connector = {
  id: 'mfapi-extended',
  displayName: 'Mutual Funds (MFAPI + AMFI + CAS upload)',
  category: 'mutual-fund',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.folioCount,
      disabledReason: null,
    };
  },

  async connect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-mfapi-read', 'MFAPI + AMFI public + CAS upload');
    const connectedAt = isoTs();
    writeStatus({ connected: true, lastSync: connectedAt, folioCount: 0 });
    return ok({ connectedAt });
  },

  async import(_ctx: ConnectorContext, opts?: { readonly fileBlob?: Blob }): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    if (opts?.fileBlob) {
      const bridge = getPdfBridge();
      const extract = await bridge.extract({ blob: opts.fileBlob });
      const casRows = parseCamsCasText(extract.textFull);
      if (casRows.length > 0) {
        const folios: readonly MutualFundFolio[] = casRows.map((r) =>
          Object.freeze<MutualFundFolio>({
            folioId: folioId(`cas_${r.folioNumber}`),
            amc: r.amc,
            schemeCode: 'unknown',
            schemeName: r.schemeName,
            units: r.units,
            latestNavPaise: paise(Math.round((r.currentValuePaise / Math.max(1, r.units)))),
            currentValuePaise: paise(r.currentValuePaise),
            investedPaise: paise(r.costPaise),
            asOf: isoTs(),
          }),
        );
        writeStatus({ ...s, lastSync: isoTs(), folioCount: folios.length });
        return ok({ mfFolios: folios });
      }
    }
    const folios = mockFolios();
    writeStatus({ ...s, lastSync: isoTs(), folioCount: folios.length });
    return ok({ mfFolios: folios });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, lastSync: null, folioCount: 0 });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-mfapi-read' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { mfFolios: mockFolios() };
  },
};

export async function fetchAmfiNavAll(): Promise<readonly AmfiNavRow[] | null> {
  try {
    const r = await fetch(AMFI_URL, { headers: { Accept: 'text/plain' } });
    if (!r.ok) return null;
    const text = await r.text();
    return parseAmfiNavAll(text);
  } catch {
    return null;
  }
}
